/**
 * @description Component-trade
 */
import React, { FC, useEffect, useMemo } from 'react';
import '../earn/index.less';
import './index.less';

import { resetFormByChainId, setTradeFormType } from '@/features/trade/actions';
import { useAppDispatch } from '@/hooks';
import { useResponsiveTradeWidth } from '@/hooks/useResponsiveTradeWidth';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';

import BlockedAlert from '@/components/Alert/BlockedAlert';
import TradeYourOwnRiskAlert from '@/components/Alert/TradeYourOwnRiskAlert';
import FavPairs from '@/components/FavPairs';
import { useMockDevTool } from '@/components/Mock';
import OrderBookSection from '@/components/OrderBookSection';
import TradeAreaOverlay from '@/components/TradeAreaOverlay';
import { FETCHING_STATUS } from '@/constants';
import { TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { useBackendChainConfig } from '@/features/config/hook';
import { useIsFetchedPairInfo } from '@/features/futures/hooks';
import { useCurrentMarketType } from '@/features/global/hooks';
import { useCurrentPairBaseInfoFromUrl, useCurrentPairFromUrl, useMetaPairMapStatus } from '@/features/pair/hook';
import { useIsFetchedPortfolioList } from '@/features/portfolio/hook';
import { useTradeStore } from '@/features/trade/store';
import { useWalletConnectStatus } from '@/features/wallet/hook';
import { WALLET_CONNECT_STATUS } from '@/types/wallet';
import { useLocalStorageState } from 'ahooks';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import PairInfo from '../components/PairInfo';
import CoinPriceChart from './CoinPriceChart';
import TradeCard from './TradeCard';
import TradePortfolio from './TradePortfolio';

interface IPropTypes {
  children?: React.ReactNode;
}
const Trade: FC<IPropTypes> = function ({}) {
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const { style: tradeRightStyle } = useResponsiveTradeWidth();
  const { showTradeAreaOverlay, setShowTradeAreaOverlay } = useTradeStore();

  const [, setLastFuturesToStorage] = useLocalStorageState('last_futures', {
    listenStorageChange: true,
  });
  const pairPageType = useCurrentMarketType();
  const userAddr = useUserAddr();

  // clear form when change page
  useEffect(() => {
    return () => {
      chainId && dispatch(resetFormByChainId({ chainId }));
    };
  }, [chainId, dispatch]);
  useEffect(() => {
    if (pairPageType) {
      setLastFuturesToStorage(pairPageType);
    }
  }, [pairPageType, setLastFuturesToStorage]);

  const walletConnectStatus = useWalletConnectStatus();
  // const { pairPageNavigate } = useSideNavigate();

  const { state } = useLocation();
  const { pairSymbol } = useParams();
  const { i18n } = useTranslation();
  const chainConfig = useBackendChainConfig(chainId);
  const currentPair = useCurrentPairFromUrl(chainId);
  const metaPairMapStatus = useMetaPairMapStatus(chainId);
  const currentBaseInfo = useCurrentPairBaseInfoFromUrl(chainId);
  // const { isExtraLarge } = useMediaQueryDevice();
  const { isMockSkeleton } = useMockDevTool();
  const marginCustomSelectorId = useMemo(
    () =>
      (chainConfig?.marketCustomPairs || []).find((item) => {
        return currentPair?.symbol && item.pairs.includes(currentPair.symbol);
      })?.id,
    [chainConfig?.marketCustomPairs, currentPair?.symbol],
  );
  const isFetchedPair = useIsFetchedPairInfo(chainId, currentBaseInfo?.instrumentAddr, currentBaseInfo?.expiry);
  const pairFuturesAlertI18n = `common.pairFuturesAlert.${chainId}.${pairSymbol}`;
  const isPairFutureAlert = i18n.exists(pairFuturesAlertI18n);
  // const currentPosition = useMainPosition(chainId, userAddr, currentBaseInfo?.id);
  // const marketSimulation = useMarketSimulation(chainId);
  // const crossMarketSimulation = useCrossMarketSimulation(chainId);
  // const removeLiquiditySimulation = useRemoveLiquidationSimulation(chainId);
  // const haveMarketSimulation = useMemo(() => {
  //   const haveRemoveLiqSimulation =
  //     !!removeLiquiditySimulation?.data &&
  //     !removeLiquiditySimulation?.data?.simulationMainPosition?.size?.eq(0) &&
  //     !removeLiquiditySimulation?.data?.simulationMainPosition?.size?.sub(currentPosition?.size || 0)?.eq(0);

  //   return haveRemoveLiqSimulation || !!marketSimulation?.data || !!crossMarketSimulation?.data;
  // }, [crossMarketSimulation?.data, currentPosition?.size, marketSimulation?.data, removeLiquiditySimulation?.data]);
  // const hasPosition = useMemo(() => {
  //   return haveMarketSimulation || currentPosition?.hasPosition;
  // }, [currentPosition?.hasPosition, haveMarketSimulation]);
  // const showTradingPairs = useThirdModalOpen(ThirdGlobalModalType.PAIR_SELECTOR);
  // const toggleTradingPairs = useToggleThirdModal(ThirdGlobalModalType.PAIR_SELECTOR);
  const isFetchedPortfolio = useIsFetchedPortfolioList(chainId, userAddr);

  const accountPositionContent = useMemo(() => {
    // if (walletConnectStatus === WALLET_CONNECT_STATUS.UN_CONNECT) {
    //   return (
    //     <div className="syn-trade-wrapper-position-unconnected">
    //       <ConnectWalletCard />
    //     </div>
    //   );
    // }
    // if (!isFetchedPortfolio || isMockSkeleton || metaPairMapStatus !== FETCHING_STATUS.DONE) {
    //   console.log('🚀 ~ isFetchedPortfolio:', isFetchedPortfolio);
    //   return <TradePositionSkeleton />;
    // }
    // check metaPairMapStatus to see rpc is working well
    // if (!isFetchedPortfolio || metaPairMapStatus !== FETCHING_STATUS.DONE) {
    //   return null;
    // }
    return (
      <>
        <TradePortfolio className="syn-trade-position-area" />
      </>
    );
  }, [walletConnectStatus]);
  // useEffect(() => {
  //   if (isExtraLarge) {
  //     toggleTradingPairs(true);
  //   }
  // }, [isExtraLarge]);

  //get all the positions orders and etc

  // go back to market when switch pair with overlay
  useEffect(() => {
    if (showTradeAreaOverlay) {
      const earnTypes = [
        TRADE_OVERLAY_TYPE.ADD_LIQ,
        TRADE_OVERLAY_TYPE.REMOVE_LIQ,
        TRADE_OVERLAY_TYPE.CLOSE_POSITION,
        TRADE_OVERLAY_TYPE.ADJUST_MARGIN,
      ];
      if (!earnTypes.includes(showTradeAreaOverlay)) {
        setShowTradeAreaOverlay(undefined);
      }
      if (!state?.toClose && chainId)
        dispatch(
          setTradeFormType({
            chainId,
            tradeType: TRADE_TYPE.MARKET,
          }),
        );
    }
  }, [currentPair?.id]);
  return (
    <div className="syn-trade-wrapper">
      <BlockedAlert />
      {!isFetchedPair && marginCustomSelectorId && (
        <TradeYourOwnRiskAlert
          style={{ marginBottom: 16, zIndex: 10 }}
          i18nId={
            isPairFutureAlert ? pairFuturesAlertI18n : `common.tradeYourOwnRiskAlert.${marginCustomSelectorId}.details`
          }
          hrefInfo={isPairFutureAlert && pairSymbol ? chainConfig?.pairFuturesAlertUrl?.[pairSymbol] : undefined}
        />
      )}
      <div className="syn-trade">
        <div className="syn-trade-left">
          <div className="syn-trade-left-top">
            {/* {isExtraLarge && showTradingPairs && <TradingPairs />} */}
            <div className="syn-trade-left-top-left">
              <FavPairs />
              {pairPageType && <PairInfo type={pairPageType} className="syn-trade-info-area" />}
              <CoinPriceChart className="syn-trade-chart-area" />
            </div>
            <OrderBookSection currentPair={currentPair} />
          </div>
          {((walletConnectStatus !== WALLET_CONNECT_STATUS.UN_CONNECT &&
            (metaPairMapStatus !== FETCHING_STATUS.DONE || !isFetchedPortfolio)) ||
            isMockSkeleton) && (
            <div className="syn-trade-left-skeletons">
              {/* <TradePositionSkeleton /> */}
              {/* <SkeletonTable
                loading={true}
                columns={[{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }]}
                rowCount={3}
              /> */}
            </div>
          )}
          {/* {!isNewPair && <>{accountPositionContent}</>} */}
          {accountPositionContent}
        </div>
        <div className="syn-trade-right" style={tradeRightStyle}>
          <TradeAreaOverlay />
          <TradeCard />
        </div>
      </div>
    </div>
  );
};

export default Trade;
